/**
 * 制品包行归并（纯函数，无 React 依赖，便于回归测试）。
 *
 * 归并语义必须与后端保持一致，否则会出现「类型重复」回归：
 * - 类型规范化对齐 bvp_back `build_bsw_package._DEFAULT_KIND_MAP`
 *   与 bsp_back `serializers._KIND_NORMALIZE_MAP`：
 *   soc/soc_ota→BSW、soc_mw→SOC_MW、soc_mw_b→SOC_MW_B、switch→SWITCH，其余大写。
 * - 优先级对齐 `apps/bcr/utils.py::merge_custom_artifacts` 的
 *   「已有 key 不覆盖」：构建产物优先，自定义产物只补缺失类型。
 *
 * 历史回归：700a79ef 曾用原始 rmp_kind 作 Map key 且改为后写覆盖，
 * 导致 soc/BSW、mcu/MCU 同时出现。此文件是唯一归并实现，改逻辑必须先改这里的测试。
 */

export interface ArtifactRow {
  key: string;
  kind: string;
  rmp_kind: string;
  name: string;
  url: string;
  isCustom?: boolean;
}

export interface UploadedArtifactLike {
  name: string;
  url: string;
  rmp_kind?: string;
}

export interface CustomArtifactLike {
  kind: string;
  name: string;
  url?: string;
}

/** 类型别名 → 规范名，与后端 _DEFAULT_KIND_MAP / _KIND_NORMALIZE_MAP 对齐 */
export const KIND_ALIASES: Record<string, string> = {
  soc: 'BSW',
  soc_ota: 'BSW',
  soc_mw: 'SOC_MW',
  soc_mw_b: 'SOC_MW_B',
  switch: 'SWITCH',
};

/** 规范化产物类型：去空白 → 别名映射 → 大写兜底 */
export const canonicalKind = (kind: string): string => {
  const k = (kind || '').trim();
  return KIND_ALIASES[k.toLowerCase()] || k.toUpperCase();
};

/** 构建产物行（key = 原始 kind，保持历史行为；仅用于归并输入） */
export const buildUploadedRows = (
  uploadedArtifacts?: Record<string, UploadedArtifactLike>,
): ArtifactRow[] => {
  if (!uploadedArtifacts) return [];
  return Object.entries(uploadedArtifacts).map(([kind, item]) => ({
    key: kind,
    kind,
    rmp_kind: item.rmp_kind || kind,
    name: item.name,
    url: item.url,
  }));
};

/** 包级自定义产物行（kind 保留原始值，供增删回写，避免二次规范化污染） */
export const buildCustomRows = (customArtifacts?: CustomArtifactLike[]): ArtifactRow[] => {
  if (!customArtifacts || customArtifacts.length === 0) return [];
  return customArtifacts.map((a, i) => ({
    key: `custom-${a.kind}-${i}`,
    kind: a.kind,
    rmp_kind: a.kind,
    name: a.name,
    url: a.url || '',
    isCustom: true,
  }));
};

/**
 * 归并构建产物与包级自定义产物。
 * 按规范类型去重，先出现者胜（构建产物在前 → 构建优先）。
 */
export const mergeArtifactRows = (
  uploadedArtifacts?: Record<string, UploadedArtifactLike>,
  customArtifacts?: CustomArtifactLike[],
): ArtifactRow[] => {
  const byKind = new Map<string, ArtifactRow>();
  for (const r of [...buildUploadedRows(uploadedArtifacts), ...buildCustomRows(customArtifacts)]) {
    const canonical = canonicalKind(r.rmp_kind);
    if (!byKind.has(canonical)) {
      byKind.set(canonical, { ...r, rmp_kind: canonical });
    }
  }
  return [...byKind.values()];
};
